/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate} from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useAppSelector} from '../../store/Store'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import {useClientSearchMutation} from '../../client/Client.api'
import { IClientDto, IClientFilter } from '../../client/Client.model'
import { ClientListItem } from '../../client/components/ClientListItem'
import {
  useAssetCreateMutation,
  useAssetFamilyGetAllMutation,
  useAssetAreaGetAllMutation,
} from '../Asset.api'
import { IAssetCreateRequestDto, IAssetArea } from '../Asset.model'
import { selectAssetFamilies, selectAssetAreas } from '../Asset.slice'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import { IFilterDto } from '../../../common/model/Core.model'
import '../../../common/css/SearchControls.css'
import classNames from 'classnames'
import {object, string} from 'yup'
import { IContractDto, IContractFilter, IContractStatus } from '../../contract/Contract.model'
import { useContractSearchMutation } from '../../contract/Contract.api'
import { ContractListItem } from '../../contract/components/ContractListItem'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IAssetMetadataDto } from '../../asset-metadata/AssetMetadata.model'
import { useAssetMetadataGetByAreaMutation } from '../../asset-metadata/AssetMetadata.api'
import { IMetadataSection } from '../../report-metadata/ReportMetadata.model'
import { ReportRenderer } from '../../../common/component/ReportRenderer'
import { COUNTRIES_EN } from '../../../util/Constants'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import ReportMetadataFormValidator from '../../report-metadata/ReportMetadataFormValidator.util'

export interface ICreateFormikSchema {
  clientId: string
  assetMetadataId: string
  contractId?: string
  familyCode: string
  family: string
  areaCode: string
  area: string
  brand: string
  model: string
  serial: string
  address: string
  countryCode?: string
  country?: string
  latitude?: string
  longitude?: string
  metadata?: IMetadataSection
}

const AssetNewPage: FC = () => {
  const intl = useIntl()
  const metadataFormValidator = new ReportMetadataFormValidator(intl);
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.ASSETS'}),
      path: '/assets',
      isSeparator: false,
      isActive: false,
    },
  ]
  
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      clientId: string().required(),
      assetMetadataId: string().required(),
      contractId: string().notRequired(),
      familyCode: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
      family: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
      areaCode: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
      area: string().required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
      brand: string()
        .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      model: string()
        .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      serial: string()
        .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      countryCode: string()
        .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      address: string()
        .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'}))
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      latitude: string()
        .notRequired()
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      longitude: string()
        .notRequired()
        .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255})),
      metadata: metadataFormValidator.validateStrict(),
    })
  )
  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    clientId: '',
    assetMetadataId: '',
    contractId: '',
    familyCode: '',
    family: '',
    areaCode: '',
    area: '',
    brand: '',
    model: '',
    serial: '',
    address: '',
    countryCode: '',
    country: '',
    latitude: '',
    longitude: '',
    metadata: undefined,
  })
  const [assetAreaResult, setAssetAreaResult] = useState<IAssetArea[] | undefined>(undefined)

  const clientFilterKeys = {
    defaultKey: IClientFilter.NAME,
    defaultKeyList: [
      IClientFilter.ID,
      IClientFilter.NAME,
      IClientFilter.SECTOR,
      IClientFilter.ADDRESS,
    ],
  }
  const [clientFilterSuggestions, setClientFilterSuggestions] = useState<string[]>(clientFilterKeys.defaultKeyList)
  const [clientFilters, setClientFilters] = useState<IFilterDto[]>([])
  const didFetchClient = useRef(false)
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [client, setClient] = useState<IClientDto>()
  const [clientSearchResult, setClientSearchResult] = useState<IClientDto[] | undefined>(undefined)

  const contractFilterKeys = {
    defaultKey: IContractFilter.ID,
    defaultKeyList: [
      IContractFilter.ID,
      IContractFilter.DOCUMENT,
      IContractFilter.SERVICE,
      IContractFilter.FREQUENCY,
    ],
  }
  const [contractFilterSuggestions, setContractFilterSuggestions] = useState<string[]>(contractFilterKeys.defaultKeyList)
  const [contractFilters, setContractFilters] = useState<IFilterDto[]>([])
  const didFetchContract = useRef(false)
  const [isContractLoading, setIsContractLoading] = useState(false)
  const [contract, setContract] = useState<IContractDto>()
  const [contractSearchResult, setContractSearchResult] = useState<IContractDto[] | undefined>(undefined)

  const didFetchAssetMetadata = useRef(false)
  const [assetMetadata, setAssetMetadata] = useState<IAssetMetadataDto | undefined>(undefined)
  const [isAssetMetadataLoading, setIsAssetMetadataLoading] = useState(false)
  const [assetMetadataGetByArea] = useAssetMetadataGetByAreaMutation()

  const [assetFamiliesGetAll] = useAssetFamilyGetAllMutation()
  const [assetAreasGetAll] = useAssetAreaGetAllMutation()
  const [contractSearch] = useContractSearchMutation()
  const [clientSearch] = useClientSearchMutation()
  const [assetCreate] = useAssetCreateMutation()

  const assetFamilies = useAppSelector(selectAssetFamilies)
  const assetAreas = useAppSelector(selectAssetAreas)

  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IAssetCreateRequestDto = {
            clientId: values.clientId,
            assetMetadataId: values.assetMetadataId,
            contractId: values.contractId,
            familyCode: values.familyCode,
            family: values.family,
            areaCode: values.areaCode,
            area: values.area,
            brand: values.brand,
            model: values.model,
            serial: values.serial,
            countryCode: values.countryCode,
            country: intl.formatMessage({id: `DROPDOWN.COUNTRY.${values.countryCode}`}),
            address: values.address,
            latitude: values.latitude,
            longitude: values.longitude,
            metadata: values.metadata,
          }

          const response: any = await assetCreate(request).unwrap()
          if (response && response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/assets/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })
  
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setClientSearchResult(undefined)
        setContractSearchResult(undefined)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    if (!assetFamilies || assetFamilies.length === 0) {
      assetFamiliesGetAll()
    }
    if (!assetAreas || assetAreas.length === 0) {
      assetAreasGetAll()
    }

    const fetchClient = async (clientId) => {
      try {
        if (!didFetchClient.current) {
          setIsClientLoading(true)
          const defaultFilters = [{id: IClientFilter.HASH, value: clientId, editable: true}]
          const response: any = await clientSearch({filters: defaultFilters})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const client: IClientDto = response.data.data[0]
            setClient(client)

            formik.setFieldValue('clientId', client.id)
            formik.setFieldValue('countryCode', client.countryCode ?? '')
            formik.setFieldValue('address', client.address ?? '')

            setClientFilters(response.data.filters)
            setContractFilters([
              {id: IContractFilter.STATUS, value: IContractStatus.ACTIVE, editable: false},
              {id: IContractFilter.CLIENT_HASH, value: client.id, editable: false},
            ])
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsClientLoading(false)
      }
    }

    const fetchContract = async (contractId) => {
      try {
        if (!didFetchContract.current) {
          setIsContractLoading(true)
          const defaultFilters = [
            {id: IContractFilter.HASH, value: contractId, editable: true},
            {id: IContractFilter.STATUS, value: IContractStatus.ACTIVE, editable: false},
          ]
          const response: any = await contractSearch({filters: defaultFilters})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const contract: IContractDto = response.data.data[0]
            setContract(contract)

            formik.setFieldValue('contractId', contract.id)
 
            setContractFilters(response.data.filters)
            await fetchClient(contract.client.id)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsContractLoading(false)
      }
    }

    const contractId = queryParams.get('contractId')
    const clientId = queryParams.get('clientId')
    if (clientId) {
      fetchClient(clientId)
    } else if (contractId) {
      fetchContract(contractId)
    }
  }, [])

  const fetchAssetMedatada = async (familyCode, areaCode) => {
    try {
      if (!didFetchAssetMetadata.current) {
        setIsAssetMetadataLoading(true)
        const response: any = await assetMetadataGetByArea({familyCode, areaCode})
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          const assetMetadata: IAssetMetadataDto = response.data
          setAssetMetadata(assetMetadata)

          formik.setFieldValue('assetMetadataId', assetMetadata.id)
          formik.setFieldValue('metadata', assetMetadata.metadata)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsAssetMetadataLoading(false)
    }
  }

  const familyOnChangeHandler = async (family) => {
    const areas = assetAreas.filter((assetArea) => assetArea.family === family)
    if (areas && areas.length > 0) {
      setAssetAreaResult(areas)
      formik.setFieldValue('familyCode', areas[0].familyCode)
      formik.setFieldValue('family', areas[0].family)
      formik.setFieldValue('area', '')
    } else {
      setAssetAreaResult(undefined)
      formik.setFieldValue('family', '')
      formik.setFieldValue('area', '')
      formik.setFieldValue('assetMetadataId', '')
      formik.setFieldValue('metadata', '')
    }
  }

  const areaOnChangeHandler = async (area) => {
    const assetArea = assetAreaResult?.find((assetArea) => assetArea.area === area)
    if (assetArea) {
      formik.setFieldValue('areaCode', assetArea.areaCode)
      formik.setFieldValue('area', assetArea.area)
      await fetchAssetMedatada(formik.values.familyCode, assetArea.areaCode)
    } else {
      formik.setFieldValue('area', '')
      formik.setFieldValue('assetMetadataId', '')
      formik.setFieldValue('metadata', '')
    }
  }

  const clientRowOnClickHandler = async (clientId) => {
    if (clientSearchResult) {
      const client = clientSearchResult.find((client) => client.id === clientId)
      if (client) {
        setClient(client)
        setClientSearchResult(undefined)
        setClientFilters([])
        setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

        formik.setFieldValue('clientId', client.id)
        formik.setFieldValue('countryCode', client.countryCode ?? '')
        formik.setFieldValue('address', client.address ?? '')

        setContractFilters([
          {id: IContractFilter.STATUS, value: IContractStatus.ACTIVE, editable: false},
          {id: IContractFilter.CLIENT_HASH, value: client.id, editable: false},
        ])
      }
    }
  }

  const clientRemoveOnClickHandler = async () => {
    setClient(undefined)
    setClientSearchResult(undefined)
    setClientFilters([])
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

    setContract(undefined)
    setContractSearchResult(undefined)
    setContractFilters([])
    setContractFilterSuggestions(contractFilterKeys.defaultKeyList)

    formik.setFieldValue('clientId', '')
    formik.setFieldValue('contractId', '')
    formik.setFieldValue('countryCode', '')
  }

  const clientSearchOnClickHandler = async () => {
    try {
      if (!didFetchClient.current) {
        setIsClientLoading(true)
        const response: any = await clientSearch({
          filters: clientFilters,
        })
        if (response && response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setClientSearchResult(response.data.data)
          setClientFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  const clientResetSearchResetOnClickHandler = async () => {
    setClientSearchResult(undefined)
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)
  }

  const contractRowOnClickHandler = async (contractId) => {
    if (client && contractSearchResult) {
      const contract = contractSearchResult.find((contract) => contract.id === contractId)
      if (contract) {
        setContract(contract)
        setContractSearchResult(undefined)
        setContractFilters([])
        setContractFilterSuggestions(contractFilterKeys.defaultKeyList)

        formik.setFieldValue('contractId', contract.id)
      }
    }
  }

  const contractRemoveOnClickHandler = async () => {
    setContract(undefined)
    setContractSearchResult(undefined)

    if (client) {
      setContractFilters([
        {id: IContractFilter.CLIENT_HASH, value: client.id, editable: false},
        {id: IContractFilter.STATUS, value: IContractStatus.ACTIVE, editable: false},
      ])
      setContractFilterSuggestions(contractFilterKeys.defaultKeyList)
    } 
    
    formik.setFieldValue('contractId', '')
  }

  const contractSearchOnClickHandler = async () => {
    try {
      if (client && !didFetchContract.current) {
        setIsContractLoading(true)
        const response: any = await contractSearch({
          filters: contractFilters,
        })
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setContractSearchResult(response.data.data)
          setContractFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsContractLoading(false)
    }
  }

  const contractResetSearchResetOnClickHandler = async () => {
    setContractSearchResult(undefined)
    setContractFilterSuggestions(contractFilterKeys.defaultKeyList)
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.clientId && formik.errors.clientId) ||
          (formik.touched.family && formik.errors.family) ||
          (formik.touched.area && formik.errors.area) ||
          (formik.touched.brand && formik.errors.brand) ||
          (formik.touched.model && formik.errors.model) ||
          (formik.touched.serial && formik.errors.serial) ||
          (formik.touched.address && formik.errors.address) ||
          (formik.touched.countryCode && formik.errors.countryCode) ||
          (formik.touched.latitude && formik.errors.latitude) ||
          (formik.touched.longitude && formik.errors.longitude)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.metadata && formik.errors.metadata
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {intl.formatMessage({id: 'UTIL.CREATE.ASSET'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!formik.isSubmitting && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/assets`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  {assetMetadata && assetMetadata.metadata.items && (
                    <li className='nav-item'>
                      <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                        {tabWithError('kt_tab_pane_2') && (
                          <span className='text-danger me-1'>*</span>
                        )}
                        {intl.formatMessage({id: 'UTIL.DETAILS'})}
                      </a>
                    </li>
                  )}
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      {!client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span
                              className={classNames('required', {
                                'text-gray-700': !(
                                  formik?.touched?.clientId && formik?.errors?.clientId
                                ),
                                'text-danger':
                                  formik?.touched?.clientId && formik?.errors?.clientId,
                              })}
                            >
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <TagInputSearchListControls
                              loading={isClientLoading || isContractLoading}
                              enabled={!formik.isSubmitting}
                              filterKeys={clientFilterKeys}
                              filterSuggestions={clientFilterSuggestions}
                              filters={clientFilters}
                              createOnClickPath={'/clients/new'}
                              setFilterSuggestions={setClientFilterSuggestions}
                              setFilters={setClientFilters}
                              searchOnClickHandler={clientSearchOnClickHandler}
                              resetSearchResetOnClickHandler={clientResetSearchResetOnClickHandler}
                            ></TagInputSearchListControls>
                          </div>
                          <label className='col-xxl-3'></label>
                          <div className='col-xxl-9 h-3px'>
                            {clientSearchResult && clientSearchResult?.length > 0 && (
                              <ul
                                id='report-client-autocomplete-list'
                                className='suggestions-list'
                                role='listbox'
                              >
                                {clientSearchResult.map((item, index) => (
                                  <li key={index} onClick={() => clientRowOnClickHandler(item.id)}>
                                    <ClientListItem data={item}></ClientListItem>
                                    <div className='separator'></div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </>
                      )}

                      {client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span className='text-gray-700 required'>
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <ClientListItem
                              disabled={formik.isSubmitting}
                              data={client}
                              rowSelected={client.id}
                              removeOnClick={clientRemoveOnClickHandler}
                            ></ClientListItem>
                          </div>
                        </>
                      )}
                    </div>

                    {client && (
                      <div className='row mb-5'>
                        {!contract && (
                          <div className='px-5'>
                            <div className='row mb-5'>
                              <div className='row mb-2'>
                                <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span
                                    className={classNames({
                                      'text-gray-700': !(
                                        formik?.touched?.contractId && formik?.errors?.contractId
                                      ),
                                      'text-danger':
                                        formik?.touched?.contractId && formik?.errors?.contractId,
                                    })}
                                  >
                                    {intl.formatMessage({id: 'UTIL.CONTRACT'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9'>
                                  <TagInputSearchListControls
                                    loading={isContractLoading || isClientLoading}
                                    enabled={!formik.isSubmitting}
                                    filterKeys={contractFilterKeys}
                                    filterSuggestions={contractFilterSuggestions}
                                    filters={contractFilters}
                                    createOnClickPath={`/contracts/new?clientId=${client.id}`}
                                    setFilterSuggestions={setContractFilterSuggestions}
                                    setFilters={setContractFilters}
                                    searchOnClickHandler={contractSearchOnClickHandler}
                                    resetSearchResetOnClickHandler={
                                      contractResetSearchResetOnClickHandler
                                    }
                                  ></TagInputSearchListControls>
                                </div>
                              </div>

                              <div className='row mb-2'>
                                <label className='col-xxl-3 text-gray-700 col-form-label'></label>
                                <div className='col-xxl-9 h-5px'>
                                  {contractSearchResult && contractSearchResult.length > 0 && (
                                    <ul
                                      id='autocomplete-list'
                                      className='suggestions-list'
                                      role='listbox'
                                    >
                                      {contractSearchResult.map((contract, index) => (
                                        <li
                                          key={index}
                                          onClick={() => contractRowOnClickHandler(contract.id)}
                                        >
                                          <ContractListItem
                                            data={contract}
                                            rowSelected={contract.id}
                                          ></ContractListItem>
                                          <div className='separator'></div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {contract && (
                          <div className='px-5'>
                            <div className='row mb-20'>
                              <div className='row mb-5'>
                                <label className='d-flex flex-column-auto flex-start align-items-start col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                                  <span className='text-gray-700'>
                                    {intl.formatMessage({id: 'UTIL.CONTRACT'})}
                                  </span>
                                </label>

                                <div className='col-xxl-9 h-5px'>
                                  <ContractListItem
                                    disabled={formik.isSubmitting}
                                    data={contract}
                                    rowSelected={contract.id}
                                    removeOnClick={contractRemoveOnClickHandler}
                                  ></ContractListItem>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSET_FAMILY'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='family'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('family')}
                                onChange={(e) => {
                                  formik.handleChange(e)
                                  familyOnChangeHandler(e.target.value)
                                }}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'ASSET.FAMILY.DROPDOWN.DEFAULT'})}
                                </option>
                                {assetFamilies &&
                                  assetFamilies.map((family, index) => (
                                    <option key={index} value={family.family}>
                                      {family.family}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSET_AREA'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='area'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('area')}
                                onChange={(e) => {
                                  formik.handleChange(e)
                                  areaOnChangeHandler(e.target.value)
                                }}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'ASSET.AREA.DROPDOWN.DEFAULT'})}
                                </option>
                                {assetAreaResult &&
                                  assetAreaResult?.map((assetType, index) => (
                                    <option key={index} value={assetType.area}>
                                      {assetType.area}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSET_BRAND'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ASSET_BRAND'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='brand'
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSET_MODEL'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ASSET_MODEL'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='model'
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ASSET_SERIAL'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ASSET_SERIAL'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='serial'
                            enableInputLength={true}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.LOCATION'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='countryCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('countryCode')}
                                onChange={formik.handleChange}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {COUNTRIES_EN &&
                                  COUNTRIES_EN.map((country, index) => (
                                    <option key={index} value={country.code}>
                                      {intl.formatMessage({id: `DROPDOWN.COUNTRY.${country.code}`})}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            placeholder={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='address'
                            enableInputLength={true}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      {assetMetadata && assetMetadata.metadata && (
                        <ReportRenderer formik={formik} path={`metadata.items`} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

export {AssetNewPage}
